import { FunctionComponent, useContext, useEffect, useMemo, useRef, useState } from 'react'
import Swiper from 'react-id-swiper'
import styled, { ThemeContext } from 'styled-components'

import { LeftChevron, RightChevron } from 'components/SVG/Chevron'

import { useWindowSize } from 'services/hooks'
import { ImageTypeEnum, getImageUrl } from 'services/images'

import * as T from 'types'

const SLIDES_PER_VIEW = 3.5
const SPACE_BETWEEN = 10

const Container = styled.div`
  width: 100%;
  box-sizing: border-box;
  margin: 0px;
  min-width: 0px;
  position: relative;
  display: flex;
`

const ThumbnailContainer = styled.div`
  width: 100%;
  margin: 10px 0;
`

const Primary = styled.div`
  width: 100%;
  left: 0;
  right: 0;
  bottom: 0;
  height: 43px;
  position: absolute;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  font-weight: 500;
  font-size: 14px;
  line-height: 14px;
  color: ${props => props.theme.colors.white};
  background: ${props => props.theme.colors.transparentBlackAlpha60};
  border-bottom-left-radius: 4px;
  border-bottom-right-radius: 4px;
`

interface IBackgroundProps {
  isViewListingPage?: boolean
}
const Slide = styled.div<IBackgroundProps>`
  width: 1000px; /* this gets reset by swiper package but must be set in px here first */
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;

  height: 300px;
  @media (min-width: ${props => props.theme.metrics.phone}px) {
    border-radius: 4px;
  }
  @media (min-width: ${props => props.theme.metrics.tablet}px) {
    height: ${props => (props.isViewListingPage ? 495 : 556)}px;
  }
`

interface IPhotoImageProps {
  objectFit?: string
}
const PhotoImage = styled.img<IPhotoImageProps>`
  position: absolute;
  object-fit: ${props => props.objectFit || 'contain'};
  width: 100%;
  height: 100%;
`

interface IThumbnailProps {
  active?: boolean
  isViewListingPage?: boolean
}
const ThumbnailSlide = styled(Slide)<IThumbnailProps>`
  box-sizing: border-box;
  border-radius: 4px;
  border: ${props => (props.active ? `5px solid ${props.theme.colors.yellow}` : 'none')};
  cursor: pointer;
  height: 80px;
  background-size: cover;

  &:first-of-type {
    margin-left: 0px;
  }

  @media (min-width: ${props => props.theme.metrics.tablet}px) {
    height: ${props => (props.isViewListingPage ? '120px' : '130px')};
  }
`

const LeftCircle = styled.div`
  position: absolute;
  top: 50%;
  left: 10px;
  height: 45px;
  width: 45px;
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 100%;
  background-color: ${props => props.theme.colors.white};
  margin-top: -22.5px;
  z-index: 10;
  padding: 10px;

  .swiper-button-prev {
    height: 15px;
    margin: -8px 0 0 -2px;
  }
`

const RightCircle = styled(LeftCircle)`
  right: 10px;
  left: unset;

  .swiper-button-next {
    height: 15px;
    margin: -8px -2px 0 0;
  }
`

const SliderCounter = styled.div`
  position: absolute;
  right: 10px;
  bottom: 10px;
  background: ${props => props.theme.colors.transparentBlackAlpha50};
  border-radius: 4px;
  font-size: 16px;
  line-height: 22px;
  text-align: center;
  color: ${props => props.theme.colors.white};
  z-index: 10;
  padding: 10px 10px;
  display: flex;
  align-items: center;
`

const getOptimizedImage = (listingId: string, photo: T.IPhoto) => ({
  ...photo,
  url: getImageUrl(listingId, photo, ImageTypeEnum.ViewListingSlide),
})

interface IProps {
  listing: T.IListing
  photos: T.IPhoto[]
  isViewListingPage?: boolean
}

const PhotoCarousel: FunctionComponent<IProps> = ({ listing, photos, isViewListingPage }) => {
  const theme = useContext(ThemeContext)

  const { width } = useWindowSize()

  const gallerySwiperParams = {
    slidesPerView: 1,
    spaceBetween: 10,
    renderPrevButton: () => (
      <LeftCircle>
        <LeftChevron
          className="swiper-button-prev"
          fill={theme.colors.black}
        />
      </LeftCircle>
    ),
    renderNextButton: () => (
      <RightCircle>
        <RightChevron
          className="swiper-button-next"
          fill={theme.colors.black}
        />
      </RightCircle>
    ),
    navigation: {
      nextEl: '.swiper-button-next',
      prevEl: '.swiper-button-prev',
    },
  }

  // calculate `thumbnailSwiperParams` slidesOffsetAfter value
  const {
    listingFlowGutter,
    listingFlowGutterPhone,
    sidebarWidth: listingFlowSidebarWidth,
    publicListingMaxWidth,
    publicListingGutter,
    publicListingSidebarWidth,
    publicListingSidebarTabletWidth,
    publicListingSpacer,
  } = theme.metrics

  let swiperWidth = width || theme.metrics.desktop

  // carousel in phone viewport in listing flow is not full width
  if (!isViewListingPage && width && width < theme.metrics.tablet) {
    swiperWidth -= listingFlowGutterPhone * 2
  }

  // tablet and dekstops have to interpret their width - padding and - sidebar
  if (width && width >= theme.metrics.tablet) {
    // desktop
    if (width >= theme.metrics.desktop) {
      const desktopWidth = width >= publicListingMaxWidth ? publicListingMaxWidth : width

      if (!isViewListingPage) {
        // listing flow sidebar has a different width than when outside of listing flow
        swiperWidth = width - listingFlowGutter * 2 - listingFlowSidebarWidth
      } else {
        swiperWidth
          = desktopWidth - publicListingGutter * 2 - publicListingSidebarWidth - publicListingSpacer
      }
    } else if (!isViewListingPage) {
      // tablet in listing flow is full width minus padding
      swiperWidth -= listingFlowGutter * 2
    } else {
      // tablet outside of listing flow has a sidebar
      swiperWidth
        = width - publicListingGutter * 2 - publicListingSidebarTabletWidth - publicListingSpacer
    }
  }

  const spaceBetween = SPACE_BETWEEN
  swiperWidth += spaceBetween

  const slidesPerView = SLIDES_PER_VIEW

  const slideWidth = swiperWidth / slidesPerView

  // calculation to leave one thumbnail hanging when you get to the last slide
  // @FIXME: currently, changing the viewport width after first render will not rerender the
  // underlying swiper component with new thumbnail params, so these values get out of sync
  let slidesOffsetAfter = swiperWidth - slideWidth

  if (photos && photos.length < slidesPerView) {
    slidesOffsetAfter -= (slidesPerView - photos.length) * slideWidth
  }

  const thumbnailSwiperParams = {
    spaceBetween,
    centeredSlides: false,
    slidesPerView,
    touchRatio: 0.2,
    slideToClickedSlide: true,
    slidesOffsetAfter,
  }

  const [currentIndex, setCurrentIndex] = useState(0)

  const gallerySwiperRef = useRef<any>(null)
  const thumbnailSwiperRef = useRef<any>(null)
  useEffect(() => {
    const gallerySwiper = gallerySwiperRef.current?.swiper
    const thumbnailSwiper = thumbnailSwiperRef.current?.swiper

    if (
      gallerySwiper
      && gallerySwiper.controller
      && thumbnailSwiper
      && thumbnailSwiper.controller
    ) {
      gallerySwiper.controller.control = thumbnailSwiper
      thumbnailSwiper.controller.control = gallerySwiper
    }

    if (thumbnailSwiper) {
      thumbnailSwiper.on('slideChange', () => setCurrentIndex(thumbnailSwiper.realIndex))
    }

    return () => {
      if (thumbnailSwiper) {
        thumbnailSwiper.off('slideChange', () => setCurrentIndex(thumbnailSwiper.realIndex))
      }
    }
  }, [])

  const optimizedPhotos = useMemo(
    () => photos.map(photo => getOptimizedImage(listing._id, photo)),
    [photos, listing._id],
  )

  return (
    <>
      <Container>
        <Swiper
          {...gallerySwiperParams}
          ref={gallerySwiperRef}
        >
          {optimizedPhotos.map(photo => (
            <Slide
              isViewListingPage={isViewListingPage}
              key={photo.url}
            >
              <PhotoImage src={photo.url} />
              {!isViewListingPage && photo.primary && <Primary>PRIMARY PHOTO</Primary>}
            </Slide>
          ))}
        </Swiper>
        {isViewListingPage && (
          <SliderCounter>{`${currentIndex + 1}/${photos.length}`}</SliderCounter>
        )}
      </Container>
      <Container>
        <ThumbnailContainer>
          <Swiper
            {...thumbnailSwiperParams}
            ref={thumbnailSwiperRef}
          >
            {optimizedPhotos.map((photo: T.IPhoto, index: number) => (
              <ThumbnailSlide
                active={index === currentIndex}
                key={photo.url}
                isViewListingPage={isViewListingPage}
              >
                <PhotoImage
                  src={photo.url}
                  objectFit="cover"
                />
              </ThumbnailSlide>
            ))}
          </Swiper>
        </ThumbnailContainer>
      </Container>
    </>
  )
}

export default PhotoCarousel
